import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function VerAgencia() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();

  const [agencia, setAgencia] = useState<any>(null);
  const [propiedades, setPropiedades] = useState<any[]>([]);
  const [proyectos, setProyectos] = useState<any[]>([]);
  const [afiliados, setAfiliados] = useState<any[]>([]);
  const [asociados, setAsociados] = useState<any[]>([]);

  const [autorizado, setAutorizado] = useState<boolean>(false);
  const [verificado, setVerificado] = useState<boolean>(false); // 🔸 nuevo estado

  useEffect(() => {
    const fetchData = async () => {
      try {
        const snap = await getDoc(doc(db, 'inmobiliaria', id!));
        if (!snap.exists()) {
          setVerificado(true);
          return;
        }

        const data = snap.data() as {
          nombre: string;
          correo: string;
          telefono: string;
          [key: string]: any;
        };

        setAgencia({ ...data, id: snap.id });

        // ✅ Authorization using doc.id
        const isAdmin = !!user?.tipo?.includes('admin');
        const isAsociadoAutorizado = !!(
          user?.tipo?.includes('asociado') &&
          user?.inmobiliaria_doc_id === id
        );

        const accesoPermitido = isAdmin || isAsociadoAutorizado;
        setAutorizado(accesoPermitido);

        if (!accesoPermitido) {
          setVerificado(true);
          return;
        }

        // Solo propiedades MLS activas de esta agencia
        const propSnap = await getDocs(
          query(
            collection(db, 'propiedades'),
            where('inmobiliaria_doc_id', '==', id),
            where('fuente', '==', 'flexmls'),
            where('estado', '==', 'A')
          )
        );
        setPropiedades(propSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const projSnap = await getDocs(
          query(collection(db, 'proyectos'), where('inmobiliaria_doc_id', '==', id))
        );
        setProyectos(projSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const afSnap = await getDocs(
          query(collection(db, 'asesor'), where('inmobiliaria_doc_ids', 'array-contains', id))
        );
        // Filtrar solo activos o sin estatus (por defecto activos)
        const afiliadosActivos = afSnap.docs.filter(doc => {
          const data = doc.data();
          return !data.estatus || data.estatus === 'activo';
        });
        setAfiliados(afiliadosActivos.map(doc => ({ id: doc.id, ...doc.data() })));

        const asSnap = await getDocs(
          query(collection(db, 'asociado'), where('inmobiliaria_doc_id', '==', id))
        );
        setAsociados(asSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        setVerificado(true); // ✅ ahora sí, después de todo
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setVerificado(true); // evitar que se quede colgado
      }
    };

    if (id && user) fetchData();
  }, [id, user]);

  // 🔸 Mientras no esté verificado, muestra "Cargando..."
  if (!verificado) {
    return (
      <div className="container py-5 text-center">
        <p className="text-muted">Cargando información de la inmobiliaria...</p>
      </div>
    );
  }

  // 🔸 Si ya se verificó y no tiene permisos
  if (!autorizado) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger text-center shadow-sm rounded">
          <h4 className="mb-2">
            <i className="fa-solid fa-triangle-exclamation me-2"></i>
            Acceso denegado
          </h4>
          <p>No tienes permisos para ver esta inmobiliaria.</p>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/dashboard')}>
            Regresar al Dashboard
          </button>
        </div>
      </div>
    );
  }

 
  
  return (
    <>
      <UserNav />
      <div className="container mt-5">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Administración de Agencias', path: '/admin-inm' },
            { label: agencia.nombre }
          ]}
        />
        {/* Header Section */}
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h2 className="mb-3 text-primary">
                  <i className="fas fa-building me-2"></i>
                  {agencia.nombre}
                </h2>
                <div className="row">
                  <div className="col-md-6">
                    <p className="mb-2"><strong><i className="fas fa-envelope me-2 text-muted"></i>Correo:</strong> {agencia.correo}</p>
                    <p className="mb-2"><strong><i className="fas fa-phone me-2 text-muted"></i>Teléfono:</strong> {agencia.telefono}</p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-2"><strong><i className="fas fa-briefcase me-2 text-muted"></i>Años de experiencia:</strong> {agencia.años_experiencia || 'N/A'}</p>
                    <p className="mb-2">
                      <strong><i className="fas fa-circle me-2 text-muted"></i>Estado:</strong>
                      <span className={`badge ms-2 ${agencia.activo !== false ? 'bg-success' : 'bg-danger'}`}>
                        {agencia.activo !== false ? 'Activa' : 'Inactiva'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              {/* Acciones */}
              <div className="col-md-4 text-md-end mt-3 mt-md-0">
                <div className="d-flex flex-column gap-2 align-items-md-end">
                  {agencia.verificacion_pendiente && (
                    <button
                      className="btn btn-success"
                      onClick={async () => {
                        await updateDoc(doc(db, 'inmobiliaria', id!), { verificacion_pendiente: false });
                        setAgencia((prev: any) => ({ ...prev, verificacion_pendiente: false }));
                      }}
                    >
                      <i className="fas fa-check-circle me-2"></i>Marcar como verificada
                    </button>
                  )}
                  <button
                    className="btn btn-outline-warning btn-sm"
                    onClick={() => navigate(`/edit-inmobiliaria/${id}`)}
                  >
                    <i className="fas fa-edit me-1"></i>Editar datos
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Banner pendiente verificacion */}
        {agencia.verificacion_pendiente && (
          <div className="alert alert-warning d-flex align-items-center mb-4">
            <i className="fas fa-exclamation-triangle me-3 fa-lg"></i>
            <div>
              <strong>Agencia pendiente de verificacion.</strong> Fue creada automaticamente por el sync MLS.
              Revisa los datos y haz clic en "Marcar como verificada" si es correcta.
              {agencia.mls_office_key && (
                <span className="d-block mt-1"><small className="text-muted">MLS Office Key: <code>{agencia.mls_office_key}</code></small></span>
              )}
            </div>
          </div>
        )}

        {agencia.creado_auto && !agencia.verificacion_pendiente && (
          <div className="alert alert-info py-2 mb-4" style={{ fontSize: '0.85rem' }}>
            <i className="fas fa-database me-2"></i>
            Agencia vinculada al MLS. Las propiedades se sincronizan automaticamente.
            {agencia.mls_office_key && <span className="ms-2"><code>{agencia.mls_office_key}</code></span>}
          </div>
        )}

        {/* Statistics Cards */}
        <div className="row mb-4">
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card bg-primary text-white h-100">
              <div className="card-body text-center">
                <i className="fas fa-home fa-2x mb-2"></i>
                <h4>{propiedades.length}</h4>
                <p className="mb-0">Propiedades</p>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card bg-success text-white h-100">
              <div className="card-body text-center">
                <i className="fas fa-project-diagram fa-2x mb-2"></i>
                <h4>{proyectos.length}</h4>
                <p className="mb-0">Proyectos</p>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card bg-info text-white h-100">
              <div className="card-body text-center">
                <i className="fas fa-users fa-2x mb-2"></i>
                <h4>{afiliados.length}</h4>
                <p className="mb-0">Afiliados</p>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card bg-warning text-white h-100">
              <div className="card-body text-center">
                <i className="fas fa-user-tie fa-2x mb-2"></i>
                <h4>{asociados.length}</h4>
                <p className="mb-0">Asociados</p>
              </div>
            </div>
          </div>
        </div>

        {/* --- PROPIEDADES --- */}
        {propiedades.length > 0 && (
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light">
              <h4 className="mb-0"><i className="fas fa-home me-2"></i>Propiedades MLS ({propiedades.length})</h4>
            </div>
            <div className="card-body">
              <div className="row">
                {propiedades.slice(0, 6).map((p) => (
                  <div className="col-md-4 mb-3" key={p.id}>
                    <div
                      className="card shadow-sm h-100"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/view-admin-property/${p.id}`)}
                    >
                      <div className="card-body">
                        <h6 className="card-title text-truncate mb-1">{p.titulo || p.nombre || 'Sin titulo'}</h6>
                        <div className="d-flex gap-1 mb-2 flex-wrap">
                          <span className="badge bg-primary" style={{ fontSize: '0.65rem' }}>MLS</span>
                          {p.tipo_propiedad && <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>{p.tipo_propiedad}</span>}
                        </div>
                        <p className="text-success fw-bold mb-1" style={{ fontSize: '0.9rem' }}>
                          {p.precio_actual_usd ? `$${p.precio_actual_usd.toLocaleString()} USD` : 'Sin precio'}
                        </p>
                        <small className="text-muted">
                          <i className="fas fa-map-marker-alt me-1"></i>
                          {p.zona || p.direccion?.area || p.ciudad || '-'}
                        </small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* --- PROYECTOS --- */}
        {proyectos.length > 0 && (
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light">
              <h4 className="mb-0"><i className="fas fa-project-diagram me-2"></i>Proyectos ({proyectos.length})</h4>
            </div>
            <div className="card-body">
              <div className="row">
                {proyectos.slice(0, 6).map((p) => (
                  <div className="col-md-4 mb-3" key={p.id}>
                    <div
                      className="card shadow-sm h-100"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ver-project/${p.id}`)}
                    >
                      <div className="card-body">
                        <h5 className="card-title text-truncate">{p.nombre || 'Sin nombre'}</h5>
                        <p className="text-muted small">{p.descripcion?.slice(0, 80) || 'Sin descripción'}...</p>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-info"><i className="fas fa-building me-1"></i>Proyecto</small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* --- AFILIADOS --- */}
        {afiliados.length > 0 && (
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light">
              <h4 className="mb-0"><i className="fas fa-users me-2"></i>Afiliados ({afiliados.length})</h4>
            </div>
            <div className="card-body">
              <div className="row">
                {afiliados.map((a) => (
                  <div className="col-md-4 mb-3" key={a.id}>
                    <div
                      className="card shadow-sm h-100"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ver-admin-afiliado/${a.id}`)}
                    >
                      <div className="card-body">
                        <h5 className="card-title text-truncate">{a.nombre || 'Sin nombre'}</h5>
                        <p className="text-muted mb-1"><i className="fas fa-envelope me-1"></i>{a.correo || 'Sin correo'}</p>
                        <p className="text-muted mb-0"><i className="fas fa-phone me-1"></i>{a.telefono || 'Sin teléfono'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* --- ASOCIADOS --- */}
        {asociados.length > 0 && (
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light">
              <h4 className="mb-0"><i className="fas fa-user-tie me-2"></i>Asociados ({asociados.length})</h4>
            </div>
            <div className="card-body">
              <div className="row">
                {asociados.map((a) => (
                  <div className="col-md-4 mb-3" key={a.id}>
                    <div
                      className="card shadow-sm h-100"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ver-admin-asociado/${a.id}`)}
                    >
                      <div className="card-body">
                        <h5 className="card-title text-truncate">{a.nombre || 'Sin nombre'}</h5>
                        <p className="text-muted mb-1"><i className="fas fa-envelope me-1"></i>{a.correo || 'Sin correo'}</p>
                        <p className="text-muted mb-0"><i className="fas fa-phone me-1"></i>{a.telefono || 'Sin teléfono'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
